import {adjustToTimeZone, ContainerConfigurable, restrictionType} from "@ots-frontend/ots-data";
import {useCallback} from "react";
import {useNavigate} from "react-router-dom";
import {useContainerMutation} from "../../../hooks/useContainerMutation";
import {useMeterMutation} from "../../../hooks/useMeterMutation";
import {useSiteMutation} from "../../../hooks/useSiteMutation";
import {useRestrictionMutation} from "../../../hooks/useRestrictionMutation";
import {ContainerAction} from "./ContainerActionMenu";
import {DispatchFormValues} from "../../form/DispatchForm/DispatchForm";

export type UseContainerActionProps = {}

export type UseContainerActionResult = {
  onContainerActionClick: (container: ContainerConfigurable, action: ContainerAction) => void;
  onMultiContainerActionClick: (container: ContainerConfigurable[], action: ContainerAction) => void;
}

export const useContainerAction = (props: UseContainerActionProps = {}): UseContainerActionResult => {
  const {} = props

  const navigate = useNavigate();

  const {
    updateContainerActive,
    updateContainerDnu,
    updateContainerDoNotDispatch,
    updateContainerDispatchIgnoreMaxNonPrimaryVolume
  } = useContainerMutation()

  const {
    updateMeterDnu,
  } = useMeterMutation()

  const {
    updateSiteTransportViaPipeline,
    updateSiteTransportOilViaPipeline,
  } = useSiteMutation()

  const {
    createRestriction,
    updateRestriction
  } = useRestrictionMutation()

  const _onContainerActionClick = useCallback((data: ContainerConfigurable, action: ContainerAction) => {
    if (action === "EDIT") {
      navigate(`/containers/form`, {state: {id: data.id}})
    } else if (action === 'TOGGLE_ACTIVE') {
      updateContainerActive(data.id, !data.active)
    } else if (action === 'TOGGLE_DNU') {
      updateContainerDnu(data.id, (!(data.doNotUse ?? false)))
    } else if (action === 'TOGGLE_DO_NOT_DISPATCH') {
      updateContainerDoNotDispatch(data.id, !data.doNotDispatch)
    } else if (action === 'TOGGLE_DISPATCH_IGNORE_MAX_NON_PRIMARY_VOLUME') {
      updateContainerDispatchIgnoreMaxNonPrimaryVolume(data.id, !data.dispatchIgnoreMaxNonPrimaryVolume)
    } else if (action === 'TOGGLE_SITE_TRANSPORT_VIA_PIPELINE' && data.siteId) {
      updateSiteTransportViaPipeline(data.siteId, !data.siteTransportViaPipeline)
    } else if (action === 'TOGGLE_SITE_TRANSPORT_OIL_VIA_PIPELINE' && data.siteId) {
      updateSiteTransportOilViaPipeline(data.siteId, !data.siteTransportOilViaPipeline)
    } else if (action === 'TOGGLE_LACT_METER_DNU') {
      if (data.lactMeters?.length === 1) {
        const meter = data.lactMeters[0]
        updateMeterDnu(meter.id, (!(meter.doNotUse ?? false)))
      }
    } else if (action === 'DISPATCH_REQUEST') {
      navigate("/requests/form", {
        state: {
          formDefaults: {
            pickupSite: data.siteId,
            pickupContainer: data.id,
          }
        }
      })
    } else if (action === 'DISPATCH') {
      navigate("/dispatch/form", {
        state: {
          formDefaults: {
            pickup: {
              site: data.siteId,
              container: data.id,
            },
            quantity: {
              number: 1,
              units: "loads",
            }
          }
        }
      });
    } else if (action === 'TOGGLE_REJECTION') {
      const rejectionRestrictions = data.restrictions?.filter(r => r.type === restrictionType.rejection)
      if (rejectionRestrictions && rejectionRestrictions.length > 0) {
        for (const restriction of rejectionRestrictions) {
          if (!restriction.endDttm) {
            updateRestriction({
              ...restriction.asPlainValue(),
              endDttm: adjustToTimeZone().getTime(),
            }, undefined)
          }
        }
      } else {
        createRestriction({
          objectId: data.id,
          objectType: 'CONTAINER',
          type: restrictionType.rejection.code,
          startDttm: adjustToTimeZone().getTime(),
          displayName: "Marked Rejected",
        }, undefined)
      }
    }
  }, [
    updateContainerActive,
    updateContainerDnu,
    updateContainerDoNotDispatch,
    updateContainerDispatchIgnoreMaxNonPrimaryVolume,
    updateSiteTransportViaPipeline,
    updateSiteTransportOilViaPipeline,
    updateMeterDnu,
    createRestriction,
    updateRestriction,
    navigate
  ])

  const _onMultiContainerActionClick = useCallback((data: ContainerConfigurable[], action: ContainerAction) => {
    if (data.length > 1) {
      if (action === "DISPATCH" || action === "DISPATCH_SPLIT_PICKUP" || action === "DISPATCH_SPLIT_DELIVERY") {
        const item = data[0]

        const formDefaults: Partial<DispatchFormValues> = {
          pickup: {
            site: item.siteId,
            container: item.id,
          },
          quantity: {
            number: 1,
            units: "loads",
          },
          splits: data.slice(1).map(other => ({
            type: action === "DISPATCH_SPLIT_DELIVERY" ? "DELIVERY" : "PICKUP",
            location: {
              site: other.siteId,
              container: other.id,
            },
            quantity: {
              number: 1,
              units: "loads",
            }
          })),
        }

        navigate("/dispatch/form", {
          state: {
            formDefaults: formDefaults
          }
        });
      }
    } else {
      if (data.length === 1) {
        _onContainerActionClick(data[0], action)
      }
    }
  }, [
    navigate,
    _onContainerActionClick
  ])

  return {
    onContainerActionClick: _onContainerActionClick,
    onMultiContainerActionClick: _onMultiContainerActionClick,
  }
}
